import { Link } from "react-router-dom";
import classes from "../components/loggedout/LoggedOut.module.scss";
import { useForm } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import dddLogo from "../assets/logo.svg";
import { ThemeContext } from "../store/ThemeContext";
import LanguageSelector from "../components/UI/LanguageSelector";
import LoadingDots from "../components/UI/LoadingDots";
import { useLoggedOutHeading } from "../store/LoggedOutHeaderContext";
import { INPUT_MAX_LENGTH, USERNAME_MAX_LENGTH, validateEmailFormat, validateUsernameFormat } from "../constants";

const apiRequestAddressBase = process.env.REACT_APP_ORIGIN || window.location.origin;

const ResetPassword = () => {
    const { t, i18n } = useTranslation();
    useLoggedOutHeading(t("Forgot your password?"));
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);
    const {logo} = useContext(ThemeContext);

    const splitRef = useRef(null);
    const headingRef = useRef(null);
    const logoRowRef = useRef(null);
    const emailLabelRef = useRef(null);
    const submitBtnRef = useRef(null);
    const formPanelRef = useRef(null);
    const [infoRowTops, setInfoRowTops] = useState(null);
    const [formMinHeight, setFormMinHeight] = useState(null);

    // The success confirmation is shorter than the email/username form, which (since
    // .loginSplit's height is auto, sized to content) would otherwise visibly shrink the
    // whole card when switching between them — Figma has both states render at the same
    // fixed card size instead, with "Back to login" pushed to the bottom by the leftover
    // space. Locks the form panel's height once, while the (taller) form is showing, and
    // leaves it locked through the switch to success rather than re-measuring then.
    useLayoutEffect(() => {
        if (success) return;
        const panel = formPanelRef.current;
        if (!panel || (window.innerWidth <= 767 || window.innerHeight <= 600)) return;
        const measure = () => setFormMinHeight(panel.offsetHeight);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(panel);
        return () => ro.disconnect();
    }, [success]);

    // Pre-filled when arriving from the "Reset your password" key on the team page, which
    // signs the user out, leaves their address in sessionStorage and navigates here. Only
    // read in the initializer (React's StrictMode calls initializers twice, so it must stay
    // side-effect free); the entry is deleted after mount so a later visit starts empty.
    const [prefillEmail] = useState(() => {
        try { return sessionStorage.getItem("e2-reset-prefill-email") || ""; } catch (e) { return ""; }
    });
    useEffect(() => {
        try { sessionStorage.removeItem("e2-reset-prefill-email"); } catch (e) {}
    }, []);

    const {
        register,
        formState: { errors },
        handleSubmit,
        watch,
    } = useForm({defaultValues: {email: prefillEmail}});

    // The screen offers two ways to identify the account and asks the user to "select one
    // of the options" — so they're mutually exclusive, not additive: typing into either
    // field disables the other until it's cleared again. Enforced here rather than left to
    // a submit-time error, so the form can't be filled into an invalid shape in the first
    // place, and so only ever one of e2-email / e2-username carries a value.
    //
    // Each half is disabled only while it is itself still empty: a password manager can
    // autofill both at once (they're an "email" field and a "username" field, exactly what
    // it looks for), and disabling both then would leave the user unable to clear either
    // one — a dead form. In that case both stay editable and resetSubmit below refuses the
    // pair instead.
    const emailFilled = (watch("email") || "").trim() !== "";
    const usernameFilled = (watch("username") || "").trim() !== "";
    const emailDisabled = usernameFilled && !emailFilled;
    const usernameDisabled = emailFilled && !usernameFilled;

    // Pins the left panel's logo group to the heading's top and the button to the submit
    // button's top, same technique as Login.jsx/SignUpNew.jsx — see Login.jsx for the
    // detailed rationale. Here the button is pinned to the form's own submit button
    // (there's no secondary action like Login's Google button to pin to instead).
    useLayoutEffect(() => {
        const split = splitRef.current;
        if (!split) return;

        const updateInfoRowTops = () => {
            if ((window.innerWidth <= 767 || window.innerHeight <= 600)) {
                setInfoRowTops(null);
                return;
            }
            const heading = headingRef.current;
            const submitBtn = submitBtnRef.current;
            const logoRow = logoRowRef.current;
            const emailLabel = emailLabelRef.current;
            if (!heading || !submitBtn) return;

            const nudgeDown = 8;
            let noAccountMarginTop;
            if (logoRow && emailLabel) {
                const logoBottom = heading.offsetTop + logoRow.offsetTop + logoRow.offsetHeight;
                noAccountMarginTop = emailLabel.offsetTop - logoBottom + nudgeDown;
            }

            setInfoRowTops({
                groupTop: heading.offsetTop,
                btn: submitBtn.offsetTop,
                noAccountMarginTop,
            });
        };

        updateInfoRowTops();
        if (document.fonts?.ready) {
            document.fonts.ready.then(updateInfoRowTops);
        }
        const ro = new ResizeObserver(updateInfoRowTops);
        ro.observe(split);
        window.addEventListener('resize', updateInfoRowTops);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', updateInfoRowTops);
        };
    }, []);

    const resetSubmit = (data) => {
        setError(null);
        // Either field on its own identifies the account, so both are optional on their
        // own — but each one, when filled in, has to match the same format rules the
        // sign-up form applies (see the register() calls below). They are deliberately not
        // interchangeable: the backend's username rules are much stricter than its e-mail
        // rules, so an address put in the username box (a "user+tag@gmail.com" one above
        // all) is rejected outright rather than sent on to fail server-side.
        const email = (data.email || "").trim();
        const username = (data.username || "").trim();
        if(email === "" && username === "")
            return _setError("Please fill in at least one field");
        // Normally unreachable — each field disables the other as soon as it has content.
        // It's still checked because an autofill can populate both at once, before either
        // of those disabled states has had a chance to apply.
        if(email !== "" && username !== "")
            return _setError("Please use only one of the options above");

        var bodyFormData = new FormData();
        bodyFormData.append('e2-email', email);
        bodyFormData.append('e2-username', username);
        setLoading(true);
        axios({
            url:apiRequestAddressBase+"/ResetPassword?json=true",
            maxRedirects: 0,
            method: "post",
            data: bodyFormData,
            headers: { "Content-Type": "multipart/form-data" },
        }).then((response) => {
            setLoading(false);
            // Store the translation KEY, never the translated string: t() called here runs
            // once, at submit time, so a stored result would keep whatever language was
            // active then and never follow a later language switch. Translation happens at
            // render instead (see the success/error blocks below), which re-runs on change.
            if(response?.data?.Status !== "OK")
                return _setError("Please enter a valid username or email address");
            setSuccess(true);
        }).catch((error) => {
            setLoading(false);
            console.log(error);
        });
    };

    // This form's error messages are stored as translation keys and only run through t()
    // at render time (see the comment in resetSubmit), which leaves i18next-parser unable
    // to see them — "Please enter a valid username or email address" was consequently
    // missing from every locale file. Listing them in a t() call the parser can find keeps
    // `npm run i18n:extract` aware of them. Same trick as SignUpNew.jsx's `translations`.
    // eslint-disable-next-line no-unused-vars
    const translations = () => {
        t("Please fill in at least one field");
        t("Please enter a valid username or email address");
        t("Please use only one of the options above");
    };

    const _setError = (message) => {
        setError(message);
        setTimeout(() => {
            setError(null);
        }, 5000);
    }

    return (
        <div className={classes.loginScaleWrapper}>
        <div className={classes.loginSplit} ref={splitRef}>
            {!logo && <div className={classes.loginInfoPanel}>
                <div className={classes.loginInfoTopGroup} style={infoRowTops ? {top: infoRowTops.groupTop} : undefined}>
                    <div className={classes.loginInfoLogo} ref={logoRowRef}>
                        <img src={dddLogo} alt="" />
                        <span>DDD Invoices</span>
                    </div>
                    <div className={classes.loginInfoNoAccount} style={infoRowTops?.noAccountMarginTop !== undefined ? {marginTop: infoRowTops.noAccountMarginTop} : undefined}>
                        {t("Don't have an account?")}
                    </div>
                    <div className={classes.loginInfoBody}>
                        <p>{t("Sign up and start FREE 30 day access. Test e-invoicing, fiscalization, archiving and plugins.")}</p>
                    </div>
                </div>
                <button type="button" className={classes.loginInfoBtn} style={infoRowTops ? {top: infoRowTops.btn} : undefined} onClick={() => {window.location.href="@dashboard-sign-up";}}>{t("Start FREE")}</button>
            </div>}
            <div className={classes.loginFormPanel} ref={formPanelRef} style={formMinHeight ? {minHeight: formMinHeight} : undefined}>
                <div className={classes.loginFlagWrap} style={{display:"flex", alignItems:"center"}}>
                    <LanguageSelector imgWidth="16"/>
                </div>
                <h2 className={classes.loginHeading} ref={headingRef}>{t("Forgot your password?")}</h2>
                {!success && <>
                    <p className={classes.loginFormDescription}>{t("Please select one of the options below to confirm your identity. We will send you an email to the associated account with further instructions.")}</p>
                    {/* This page is reached logged out, so there is no way to know whose
                        account is being typed in, let alone how it was created — a Google
                        account simply has no password, and the reset mail can do nothing for
                        it. Saying so up front is the only thing possible here; the keys that
                        lead here from inside the app are hidden for those accounts instead. */}
                    <p className={classes.resetOauthHint}>
                        {t("Signed up with Google? Your account has no password — use Google on the")}{" "}
                        <Link to="/login" className={classes["main-blue-link"]}>{t("login page")}</Link>{" "}
                        {t("instead.")}
                    </p>
                    <form onSubmit={handleSubmit(resetSubmit)}>
                        <label htmlFor="email" ref={emailLabelRef} className={emailDisabled ? classes.loginFieldDisabledLabel : undefined}>{t("Email Address")}</label>
                        <input name="email" id="email" type="email" className={classes.loginInput} placeholder={t("test@company.com")} maxLength={INPUT_MAX_LENGTH} disabled={emailDisabled} {...register("email", {
                            validate: (value) => !(value || "").trim() || validateEmailFormat(value, t),
                        })}/>
                        {errors["email"] && <div style={{color: "rgb(232, 73, 14)"}}>{errors["email"].message}</div>}
                        <div className={classes["line-through"]}>
                            <span className={classes["line-text"]}>{t("or")}</span>
                        </div>
                        <label htmlFor="username" className={`${classes.resetUsernameLabel} ${usernameDisabled ? classes.loginFieldDisabledLabel : ""}`}>{t("Username")}</label>
                        <input name="username" id="username" className={classes.loginInput} placeholder={t("Your username")} autoCapitalize="none" autoCorrect="off" spellCheck="false" maxLength={USERNAME_MAX_LENGTH} disabled={usernameDisabled} {...register("username", {
                            validate: (value) => !(value || "").trim() || validateUsernameFormat(value, t),
                        })}/>
                        {errors["username"] && <div style={{color: "rgb(232, 73, 14)"}}>{errors["username"].message}</div>}
                        <div className={classes.errorSlot}>
                            {error && <div style={{color: "rgb(232, 73, 14)"}}>{t(error)}</div>}
                        </div>
                        {loading && <LoadingDots/>}
                        {!loading && <button ref={submitBtnRef} type="submit" className={classes.loginSubmitBtn} disabled={loading}>{t("Send email")}</button>}
                    </form>
                </>}
                {success && <>
                    <div className={classes.resetSuccessTitle}>{t("Check your email")}</div>
                    <p className={classes.resetSuccessBody}>{t("An email has been sent to you with instructions on how to reset your password")}</p>
                    <p className={classes.resetSuccessHint}>
                        {t("Didn't receive the email? Check your spam filter or")}{" "}
                        <button type="button" className={classes.resetSuccessHintLink} onClick={() => setSuccess(null)}>{t("try another option to confirm your identity")}</button>
                    </p>
                    <Link ref={submitBtnRef} to="/login" className={classes.loginSubmitBtn} style={{marginTop: "auto", textAlign: "center", textDecoration: "none", boxSizing: "border-box"}}>{t("Back to login")}</Link>
                </>}
            </div>
        </div>
        </div>
    );
}
export default ResetPassword;
